const AUTH_RETRY_FLAG = "__qtrackAuthRetried";

const PUBLIC_AUTH_PATHS = new Set([
  "/api/login",
  "/api/send_phone_sms",
  "/api/login_phone",
  "/api/get_salt",
  "/api/refresh",
]);

function normalizeRequestPath(url) {
  const value = String(url || "").trim();
  if (!value) return "";

  let path = value;
  try {
    path = new URL(value, "http://qtrack.local").pathname;
  } catch (_error) {
    path = value.split("?", 1)[0].split("#", 1)[0];
  }
  const normalized = `/${String(path).replace(/^\/+|\/+$/g, "")}`;
  return normalized === "/" ? normalized : normalized.replace(/\/+$/, "");
}

function isPublicAuthRequest(url) {
  return PUBLIC_AUTH_PATHS.has(normalizeRequestPath(url));
}

function unauthorizedAction(error) {
  const status = Number(error && error.response && error.response.status);
  const config = (error && error.config) || {};
  if (status !== 401 || isPublicAuthRequest(config.url)) {
    return "reject";
  }
  return config[AUTH_RETRY_FLAG] ? "redirect" : "refresh";
}

function markAuthRetry(config) {
  if (config && typeof config === "object") {
    config[AUTH_RETRY_FLAG] = true;
  }
  return config;
}

function createRedirectLatch() {
  let active = false;
  return {
    run(callback) {
      if (active) return false;
      active = true;
      callback();
      return true;
    },
    reset() {
      active = false;
    },
    isActive() {
      return active;
    },
  };
}

function createAuth401Coordinator(options) {
  const {
    clearSession,
    getToken,
    redirectToLogin,
    refreshToken,
    retryRequest,
    stopRequest,
  } = options;
  const redirectLatch = createRedirectLatch();
  let refreshPromise = null;

  function expireSession() {
    redirectLatch.run(() => {
      clearSession();
      redirectToLogin();
    });
    return stopRequest();
  }

  async function handle(error) {
    const action = unauthorizedAction(error);
    if (action === "reject") {
      throw error;
    }
    if (action === "redirect") {
      return expireSession();
    }

    const config = markAuthRetry((error && error.config) || {});
    try {
      if (!refreshPromise) {
        refreshPromise = Promise.resolve()
          .then(() => refreshToken())
          .finally(() => {
            refreshPromise = null;
          });
      }
      const refreshed = await refreshPromise;
      if (refreshed) {
        return retryRequest(config, getToken());
      }
    } catch (_error) {
      // Refresh failure is handled as an expired session below.
    }
    return expireSession();
  }

  return {
    expireSession,
    handle,
    isRedirecting: redirectLatch.isActive,
    reset: redirectLatch.reset,
  };
}

module.exports = {
  AUTH_RETRY_FLAG,
  createAuth401Coordinator,
  createRedirectLatch,
  isPublicAuthRequest,
  markAuthRetry,
  normalizeRequestPath,
  unauthorizedAction,
};
